import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { Link, NavLink } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import PayrollMenu from "./PayrollMenu";
import config from "../../Config";
import DesignationDropDown from "../Shared/DesignationDropDown";
import EmployeeTypeDropDown from "../Shared/EmployeeTypeDropDown";
import EmplyeeCampusDropDown from "../Shared/EmployeeCampusDropDown";
import PayScaleBasic from "../Shared/PayScaleBasic";
import SalaryTypeDropDown from "../Shared/SalaryTypeDropDown";
import { NumberFormat } from "../../UiComponents/common/Util";

function BulkSalaryUpdate() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [updatingId, setUpdatingId] = useState(0);

  const [salaryTypeId, setSalaryTypeId] = useState(0);
  const [listOfEmployees, setListOfEmployees] = useState([]);
  const [salaryValueMap, setSalaryValueMap] = useState({});
  const [originalSalaryMap, setOriginalSalaryMap] = useState({});
  const [remarksMap, setRemarksMap] = useState({});

  const [filters, setFilters] = useState({
    Code: "",
    CodeList: "",
    ExcludeCodeList: false,
    ID: "",
    OldSerialNo: "",
    DesignationID: 0,
    EmployeeTypeID: 0,
    Keywords: "",
    Campus_ID: 0,
    IS_Resigned: "false",
    LongLeaveStatus: "false",
    EmployementStatus: "",
    Basic_Pay_Scale: "",
    Gender: null,
    OrderBy: "OldSerialNo",
    SalaryValueFilter: "All",
  });

  const hasSalaryType = useMemo(() => Number(salaryTypeId) > 0, [salaryTypeId]);

  const normalizeCode = (value) => {
    if (value === null || value === undefined) return "";
    return value
      .toString()
      .trim()
      .replace(/^['"]+|['"]+$/g, "")
      .trim()
      .toLowerCase();
  };

  useEffect(() => {
    document.title = "Bulk Salary Update";
  }, []);

  const fetchSalaryTypeValues = async (salaryType, activeFilters) => {
    const res = await axios({
      method: "post",
      url: config.base_url + "Payroll/GetSalaryTypeValues",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        SalaryTypeId: Number(salaryType),
        OrderBy: activeFilters?.OrderBy || "OldSerialNo",
      },
    });
    const result = {};
    const remarks = {};
    (res.data?.ListofRecords || []).forEach((item) => {
      result[item.ID] = item.GrossPay ?? 0;
      remarks[item.ID] = item.Remarks ?? "";
    });
    return { values: result, remarks };
  };

  const fetchEmployees = async (overrideFilters = null) => {
    if (!hasSalaryType) {
      toast.error("Please select Salary Type first.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setLoading(true);
    try {
      const activeFilters = overrideFilters || filters;
      const hasCodeListFilter = (activeFilters.CodeList || "").trim() !== "";
      const requestData = {
        AccessKey,
        UserID,
        MemberType,
        pageNo: 1,
        pageSize: 10000,
        SchoolID: 1,
        ...activeFilters,
      };

      const empRes = await axios({
        method: "post",
        url: config.base_url + "Payroll/SearchEmployees",
        headers: { "Content-Type": "application/json" },
        data: requestData,
      });

      if (empRes.data?.status_code !== 1) {
        toast.error(empRes.data?.status_message || "Failed to load employees.", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setListOfEmployees([]);
        return;
      }

      const employees = empRes.data.ListofRecords || [];
      let filteredEmployees = employees;
      const codeListRaw = (activeFilters.CodeList || "").trim();
      if (codeListRaw !== "") {
        const codeSet = new Set(
          codeListRaw
            .split(",")
            .map((x) => normalizeCode(x))
            .filter((x) => x !== "")
        );
        const excludeMode = activeFilters.ExcludeCodeList === true;
        filteredEmployees = employees.filter((e) => {
          const matched = codeSet.has(normalizeCode(e?.Code || ""));
          return excludeMode ? !matched : matched;
        });
      }

      const salaryTypeData = await fetchSalaryTypeValues(salaryTypeId, activeFilters);
      const allSalaryValues = salaryTypeData.values || {};
      const allRemarks = salaryTypeData.remarks || {};
      const salaryValueFilter = activeFilters.SalaryValueFilter || "All";
      if (salaryValueFilter !== "All") {
        filteredEmployees = filteredEmployees.filter((e) => {
          const val = Number(allSalaryValues[e.ID] ?? 0);
          if (salaryValueFilter === "Positive") return val > 0;
          if (salaryValueFilter === "Negative") return val < 0;
          if (salaryValueFilter === "NonZero") return val !== 0;
          return true;
        });
      }

      setListOfEmployees(filteredEmployees);
      const currentMap = {};
      const existingMap = {};
      const remarkValues = {};
      filteredEmployees.forEach((e) => {
        const existingValue = allSalaryValues[e.ID] ?? 0;
        currentMap[e.ID] = existingValue;
        existingMap[e.ID] = existingValue;
        remarkValues[e.ID] = allRemarks[e.ID] ?? "";
      });
      setSalaryValueMap(currentMap);
      setOriginalSalaryMap(existingMap);
      setRemarksMap(remarkValues);
    } catch (error) {
      toast.error("Network Error.", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setLoading(false);
    }
  };

  const handleFilters = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    const nextFilters = {
      ...filters,
      Code: form.Code.value,
      CodeList: form.CodeList.value,
      ExcludeCodeList: form.ExcludeCodeList.checked,
      ID: form.ID.value,
      OldSerialNo: form.OldSerialNo.value,
      DesignationID: form.DesignationID.value,
      EmployeeTypeID: form.EmployeeTypeID.value,
      Keywords: form.Keywords.value,
      Campus_ID: form.EmployeeCampusID.value,
      IS_Resigned: form.status.value,
      LongLeaveStatus: form.longLeaveStatus.value,
      EmployementStatus: form.employmentStatus.value,
      Basic_Pay_Scale: form.Basic_Pay_Scale.value,
      SalaryValueFilter: form.SalaryValueFilter.value,
      OrderBy: form.orderBy.value,
      Gender: form.Gender.value === "All" ? null : form.Gender.value,
    };
    setFilters(nextFilters);
    fetchEmployees(nextFilters);
  };

  const handleSalaryChange = (employeeId, value) => {
    setSalaryValueMap((prev) => ({
      ...prev,
      [employeeId]: value,
    }));
  };

  const handleRemarksChange = (employeeId, value) => {
    setRemarksMap((prev) => ({
      ...prev,
      [employeeId]: value,
    }));
  };

  const copyValueDown = (fromIndex) => {
    const source = listOfEmployees[fromIndex];
    if (!source) return;
    const sourceValue = salaryValueMap[source.ID] ?? 0;
    setSalaryValueMap((prev) => {
      const next = { ...prev };
      for (let i = fromIndex; i < listOfEmployees.length; i++) {
        next[listOfEmployees[i].ID] = sourceValue;
      }
      return next;
    });
    toast.success("Value copied to all rows below.", {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  const updateOne = async (employeeId) => {
    const value = salaryValueMap[employeeId];
    setUpdatingId(employeeId);
    try {
      const res = await axios({
        method: "post",
        url: config.base_url + "Payroll/UpdateSalaryTypeValues",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey,
          UserID,
          MemberType,
          SalaryTypeId: Number(salaryTypeId),
          ListofRecords: [{
            ID: employeeId,
            GrossPay: Number(value || 0),
            Remarks: remarksMap[employeeId] ?? "",
          }],
        },
      });
      if (res.data?.status_code === 1) {
        toast.success("Salary updated successfully.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(res.data?.status_message || "Failed to update salary.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      toast.error("Network Error.", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setUpdatingId(0);
    }
  };

  const saveAll = async () => {
    if (!listOfEmployees.length) return;
    setSavingAll(true);
    try {
      const payloadRows = listOfEmployees.map((e) => ({
        ID: e.ID,
        GrossPay: Number(salaryValueMap[e.ID] ?? 0),
        Remarks: remarksMap[e.ID] ?? "",
      }));
      const res = await axios({
        method: "post",
        url: config.base_url + "Payroll/UpdateSalaryTypeValues",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey,
          UserID,
          MemberType,
          SalaryTypeId: Number(salaryTypeId),
          ListofRecords: payloadRows,
        },
      });
      if (res.data?.status_code === 1) {
        toast.success(res.data?.status_message || "All salaries updated successfully.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(res.data?.status_message || "Failed to update all salaries.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      toast.error("Network Error.", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setSavingAll(false);
    }
  };

  const existingTotal = useMemo(
    () =>
      listOfEmployees.reduce(
        (sum, e) => sum + Number(originalSalaryMap[e.ID] ?? 0),
        0
      ),
    [listOfEmployees, originalSalaryMap]
  );
  const newTotal = useMemo(
    () =>
      listOfEmployees.reduce(
        (sum, e) => sum + Number(salaryValueMap[e.ID] ?? 0),
        0
      ),
    [listOfEmployees, salaryValueMap]
  );

  return (
    <>
      <PayrollMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/Dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/PayRoll/PayRollDashboard">Payroll</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Bulk Salary Update</li>
                </ol>
              </nav>
              <div className="page-title">Bulk Salary Update</div>
              <p className="text-muted small mb-0 mt-2">
                To update all salary heads from a spreadsheet, use{" "}
                <Link to="/PayRoll/GenerateEmployeesSalary">Generate Employees Salary</Link>
                : download salary details, edit, then upload.
              </p>
            </div>
            <div className="col-md-6">
              <div className="page-right-actions">
                <button
                  onClick={() => setShowFilters((p) => !p)}
                  className="button button-white button-icon"
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                  <i className="ri-filter-3-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`collapse ${showFilters ? "show" : ""}`}>
        <div className="page-content">
          <form onSubmit={handleFilters}>
            <div className="container">
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <label>Salary Type</label>
                        <select
                          className="form-select"
                          value={salaryTypeId}
                          onChange={(e) => setSalaryTypeId(e.target.value)}
                          required
                        >
                          <option value="">Please Select</option>
                          <SalaryTypeDropDown />
                        </select>
                      </div>
                    </div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Gender</label><select name="Gender" className="form-select" defaultValue="All"><option value="All">All</option><option value="true">Male</option><option value="false">Female</option></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Campus</label><select name="EmployeeCampusID" className="form-select"><option value="">Please Select</option><EmplyeeCampusDropDown /></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Department</label><select name="EmployeeTypeID" className="form-select"><option value="">Please Select</option><EmployeeTypeDropDown /></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Designation</label><select className="form-select" name="DesignationID"><option value="">Please Select</option><DesignationDropDown /></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Status</label><select className="form-select" name="status" defaultValue="false"><option value="null">--All--</option><option value="false">Current</option><option value="true">Retired</option></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>GPS Pay Scale</label><select className="form-select" name="Basic_Pay_Scale"><option value="">--All--</option><PayScaleBasic /></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Staff Code</label><input type="text" name="Code" className="form-control" /></div></div>
                    <div className="col-md-12"><div className="form-group mb-2"><label>Employee Codes (comma separated)</label><input type="text" name="CodeList" className="form-control" placeholder="e.g. E001, E010, E099 or 'E001','E010'" /></div></div>
                    <div className="col-md-12">
                      <div className="form-group mb-3">
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" id="ExcludeCodeList" name="ExcludeCodeList" defaultChecked={false} />
                          <label className="form-check-label" htmlFor="ExcludeCodeList">
                            Exclude these codes (reverse filter)
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Staff ID</label><input type="text" name="ID" className="form-control" /></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Keywords</label><input type="text" name="Keywords" className="form-control" /></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Order by</label><select className="form-select" name="orderBy" defaultValue="OldSerialNo"><option value="OldSerialNo">Old Serial No</option><option value="Name">Alphabetic</option><option value="Date_Of_Joining ASC">DOJ ASC</option><option value="Date_Of_Joining DESC">DOJ DESC</option><option value="Code Asc">Code Asc</option><option value="Code Desc">Code Desc</option></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Long Leave Status</label><select className="form-select" name="longLeaveStatus" defaultValue="false"><option value="false">Available</option><option value="true">On Long Leave</option></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Employment Status</label><select className="form-select" name="employmentStatus" defaultValue=""><option value="">All</option><option value="P">Permanent</option><option value="T">Temporary</option></select></div></div>
                    <div className="col-md-3"><div className="form-group mb-3"><label>Old Serial No</label><input type="text" name="OldSerialNo" className="form-control" /></div></div>
                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <label>Load Records with Salary Value</label>
                        <select className="form-select" name="SalaryValueFilter" defaultValue="All">
                          <option value="All">All</option>
                          <option value="Positive">Positive Values Only</option>
                          <option value="Negative">Negative Values Only</option>
                          <option value="NonZero">Have Value</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-2 offset-md-10">
                      <div className="form-group text-end">
                        <button type="submit" className="button button-primary mt-4 w-100">Search</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="card">
            <div className="card-body">
              <table className="table table-theme">
                <thead>
                  <tr>
                    <th style={{ width: "30px" ,textWrap:"wrap"}}>ID</th>
                    <th style={{ width: "30px" ,textWrap:"wrap"}}>Serial No</th>
                    <th style={{ width: "50px" ,textWrap:"wrap"}}>Code</th>
                    <th style={{ width: "150px" ,textWrap:"wrap"}}>Employee Name</th>
                    <th style={{ width: "70px" ,textWrap:"wrap"}}>Designation</th>
                    <th style={{ width: "70px" ,textWrap:"wrap"}}>Basic Pay Scale</th>
                    <th style={{ width: "70px" ,textWrap:"wrap"}}>Existing Salary Value</th>
                    <th style={{ width: "120px" ,textWrap:"wrap"}}>New Salary Value</th>
                    <th>Remarks</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="text-center" colSpan="11">
                        <Spinner animation="border" role="status" />
                      </td>
                    </tr>
                  ) : listOfEmployees.length > 0 ? (
                    <>
                      {listOfEmployees.map((item, index) => (
                        <tr key={item.ID}>
                          <td style={{ width: "30px" ,textWrap:"wrap"}}>{item.ID}</td>
                          <td style={{ width: "30px" ,textWrap:"wrap"}}>{index + 1}</td>
                          <td style={{ width: "50px" ,textWrap:"wrap"}}>{item.Code}</td>
                          <td style={{ width: "150px" ,textWrap:"wrap"}}>{item.Name}</td>
                          <td style={{ width: "70px" ,textWrap:"wrap"}}>{item?.Selected_Designation?.Name || "-"}</td>
                          <td style={{ width: "70px" ,textWrap:"wrap"}}>{item.Basic_Pay_Scale || item.GPS_Scale || 0}</td>
                          <td style={{ width: "70px" ,textWrap:"wrap"}}>{Number(originalSalaryMap[item.ID] ?? 0)}</td>
                          
                          <td style={{ width: "120px" ,textWrap:"wrap"}}>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control form-control-sm"
                              value={salaryValueMap[item.ID] ?? 0}
                              onChange={(e) => handleSalaryChange(item.ID, e.target.value)}
                            />
                          </td>

                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={remarksMap[item.ID] ?? ""}
                              onChange={(e) => handleRemarksChange(item.ID, e.target.value)}
                            />
                          </td>

                          <td className="text-center">
                            <ul className="inline-action" aria-labelledby="dropdownMenuButton1">
                              <li>
                                <button
                                  onClick={() => copyValueDown(index)}
                                  className="blue"
                                  title="Copy value to all rows below"
                                >
                                  <i className="ri-file-copy-line"></i>
                                </button>
                              </li>
                              <li>
                                <button
                                  onClick={() => updateOne(item.ID)}
                                  className="green"
                                  title="Save Salary"
                                  disabled={updatingId === item.ID}
                                >
                                  {updatingId === item.ID ? (
                                    <i className="ri-loader-4-line"></i>
                                  ) : (
                                    <i className="ri-save-line"></i>
                                  )}
                                </button>
                              </li>
                            </ul>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="6" className="text-end">
                          <b>Total</b>
                        </td>
                        <td>
                          <b>{NumberFormat(existingTotal)}</b>
                        </td>
                        <td>
                          <b>{NumberFormat(newTotal)}</b>
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan="11" className="text-end">
                          <button
                            className="button button-primary"
                            onClick={saveAll}
                            disabled={savingAll || !hasSalaryType}
                          >
                            {savingAll ? "Saving..." : "Save All"}
                          </button>
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan="11">No Data Found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BulkSalaryUpdate;
